/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/**
 * =================================================================================================
 * Variant Auto-Selector - Selects LGE size on load; falls back to first available on color change
 * =================================================================================================
 */
/* TEMPORARILY DISABLED
document.addEventListener("DOMContentLoaded", function() {
  if (!window.location.pathname.includes("/products/")) return;

  let isAutoSelecting = false;
  let debounceTimer;

  function getSizeFieldset() {
    return [...document.querySelectorAll('fieldset.option-selector')]
      .find(fieldset => {
        const legend = fieldset.querySelector('legend, .option-selector__legend');
        if (!legend) return false;
        return (legend.innerText || legend.textContent || '').trim().toUpperCase().includes('SIZE');
      });
  }

  function selectFirstAvailableSize() {
    const sizeFieldset = getSizeFieldset();
    if (!sizeFieldset) return;
    for (const option of sizeFieldset.querySelectorAll('.opt-btn.js-option')) {
      if (!option.classList.contains('is-unavailable') && !option.classList.contains('is-nonexistent')) {
        const label = document.querySelector(`label[for="${option.id}"]`);
        if (label) {
          isAutoSelecting = true;
          label.click();
          setTimeout(() => { isAutoSelecting = false; }, 100);
          return;
        }
      }
    }
  }

  function selectLGEOnLoad() {
    if (isAutoSelecting) return;
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;
    const sizeFieldset = getSizeFieldset();
    if (!sizeFieldset) return;

    const lgeButton = [...sizeFieldset.querySelectorAll("label.opt-label")]
      .find(el => (el.innerText || el.textContent || '').trim().toUpperCase().startsWith("LGE"));

    if (lgeButton) {
      const lgeInput = document.getElementById(lgeButton.getAttribute('for'));
      if (lgeInput && !lgeInput.classList.contains('is-unavailable') && !lgeInput.classList.contains('is-nonexistent')) {
        isAutoSelecting = true;
        lgeButton.click();
        setTimeout(() => { isAutoSelecting = false; }, 100);
        return;
      }
    }

    selectFirstAvailableSize();
  }

  function handleUnavailableSize() {
    if (isAutoSelecting) return;
    const sizeFieldset = getSizeFieldset();
    if (!sizeFieldset) return;
    const selected = sizeFieldset.querySelector('.opt-btn.js-option:checked');
    if (selected && !selected.classList.contains('is-unavailable') && !selected.classList.contains('is-nonexistent')) return;
    selectFirstAvailableSize();
  }

  function debouncedHandleUnavailableSize() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(handleUnavailableSize, 100);
  }

  setTimeout(selectLGEOnLoad, 500);

  setTimeout(function() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    const allOptions = variantPicker.querySelectorAll('.opt-btn.js-option');

    allOptions.forEach(option => {
      option.addEventListener('change', debouncedHandleUnavailableSize);
    });

    const observer = new MutationObserver(debouncedHandleUnavailableSize);
    allOptions.forEach(option => {
      observer.observe(option, { attributes: true, attributeFilter: ['class', 'disabled'] });
    });
  }, 600);
});
*/

document.addEventListener("DOMContentLoaded", function () {
    const trigger = document.querySelector(".theme-product-reviews");
    const target = document.querySelector(".jdgm-rev-widg");

    if (trigger && target) {
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
});

/**
 * =================================================================================================
 * Dynamic Stock Indicator - Updates stock status based on selected variant
 * =================================================================================================
 * 
 * This code listens for variant changes and updates the stock indicator to reflect the
 * inventory quantity of the currently selected variant.
 */
document.addEventListener('on:variant:change', (event) => {
  const stockIndicator = document.querySelector('.stock-indicator');
  
  if (!stockIndicator) return;
  
  const { variant } = event.detail;
  
  if (!variant || !variant.id) return;
  
  // Parse variant inventory data from data attribute
  const variantInventoryData = stockIndicator.dataset.variantInventory;
  const inStockThreshold = parseInt(stockIndicator.dataset.inStockThreshold) || 0;
  const lowStockThreshold = parseInt(stockIndicator.dataset.lowStockThreshold) || 0;
  const restockMessage = stockIndicator.dataset.restockMessage;
  const restockDate = stockIndicator.dataset.restockDate;
  
  if (!variantInventoryData) return;
  
  // Build inventory map from data attribute (format: "variantId1:qty1,variantId2:qty2")
  const inventoryMap = {};
  variantInventoryData.split(',').forEach(item => {
    const [variantId, quantity] = item.split(':');
    inventoryMap[variantId] = parseInt(quantity) || 0;
  });
  
  // Get inventory for the selected variant
  const variantInventory = inventoryMap[variant.id] || 0;
  
  // Determine stock status
  let newStatus;
  let newText;

  // variant.available is Shopify's authoritative availability flag — it accounts for inventory
  // policy, channel publishing, selling plans, and app restrictions. Always trust it over raw
  // inventory counts; mismatches between the two are what produce contradictory messaging.
  if (variant.available === false) {
    newStatus = 'out-of-stock';
    if (restockMessage) {
      newText = restockMessage;
    } else if (restockDate) {
      newText = `OUT OF STOCK - NEW ORDERS SHIP ${restockDate}`;
    } else {
      newText = 'OUT OF STOCK';
    }
  } else if (variantInventory > inStockThreshold) {
    newStatus = 'in-stock';
    newText = 'IN STOCK - READY TO SHIP';
  } else if (variantInventory > lowStockThreshold) {
    newStatus = 'low-stock';
    newText = 'LOW STOCK - READY TO SHIP';
  } else {
    newStatus = 'out-of-stock';
    if (restockMessage) {
      newText = restockMessage;
    } else if (restockDate) {
      newText = `OUT OF STOCK - NEW ORDERS SHIP ${restockDate}`;
    } else {
      newText = 'OUT OF STOCK';
    }
  }
  
  // Update the stock indicator classes
  stockIndicator.className = `stock-indicator ${newStatus}`;
  
  // Update the text
  const stockText = stockIndicator.querySelector('.stock-text');
  if (stockText) {
    stockText.textContent = newText;
  }
});

/**
 * =================================================================================================
 * Original Price Persistence - Ensures original_price remains visible after variant changes
 * =================================================================================================
 * 
 * This code listens for variant changes and ensures that if an original_price is set in the
 * block settings, it continues to display as the "was" price even after variant changes.
 * This is especially important when variants default to LGE on page load.
 * 
 * IMPORTANT: The "was" price will only show if it's different from the current price.
 */

/**
 * Helper function to extract numeric price from formatted price string
 * Handles formats like "$19.99 USD", "$19.99", "19.99", etc.
 */
function extractNumericPrice(priceString) {
  if (!priceString) return null;
  
  // Remove currency symbols, letters, and extra spaces, keep numbers, dots, and commas
  const cleaned = priceString.replace(/[^0-9.,]/g, '');
  
  // Convert to number (handle both . and , as decimal separators)
  const price = parseFloat(cleaned.replace(',', '.'));
  
  return isNaN(price) ? null : price;
}

document.addEventListener('on:variant:change', (event) => {
  // Use setTimeout to ensure this runs after the theme's built-in updatePrice() method
  setTimeout(() => {
    const priceContainer = document.querySelector('.product-info__price');
    
    if (!priceContainer) return;
    
    const { variant } = event.detail;
    
    // Get the original price from the data attribute
    const originalPriceFormatted = priceContainer.dataset.originalPrice;
    
    // Only proceed if original_price is set
    if (!originalPriceFormatted || originalPriceFormatted === '') return;
    
    // Get the price elements
    const priceElement = priceContainer.querySelector('.price');
    const priceWasEl = priceContainer.querySelector('.price__was');
    const priceCurrentEl = priceContainer.querySelector('.price__current');
    
    if (!priceElement || !priceWasEl || !priceCurrentEl) return;
    
    // Extract numeric values for comparison
    const originalPriceValue = extractNumericPrice(originalPriceFormatted);
    const currentPriceValue = variant ? variant.price / 100 : extractNumericPrice(priceCurrentEl.textContent);
    
    // If variant exists and has a compare_at_price, use that
    // Otherwise, use the original_price from block settings
    if (variant) {
      if (!variant.compare_at_price || variant.compare_at_price <= variant.price) {
        // No compare_at_price or it's not higher than price
        // Only show original_price if it's different from current price
        if (originalPriceValue && currentPriceValue && originalPriceValue > currentPriceValue) {
          priceWasEl.innerHTML = originalPriceFormatted;
          priceElement.classList.add('price--on-sale');
        } else {
          // Prices are equal or original is lower, hide the "was" price
          priceWasEl.innerHTML = '';
          priceElement.classList.remove('price--on-sale');
        }
      }
      // If compare_at_price exists and is higher, the updatePrice() method already handles it
    } else {
      // No variant selected
      if (originalPriceValue && currentPriceValue && originalPriceValue > currentPriceValue) {
        priceWasEl.innerHTML = originalPriceFormatted;
        priceElement.classList.add('price--on-sale');
      } else {
        priceWasEl.innerHTML = '';
        priceElement.classList.remove('price--on-sale');
      }
    }
  }, 0);
});

/**
 * =================================================================================================
 * IntersectionObserver Image Lazy Loader
 * =================================================================================================
 *
 * Handles images that use the theme's "manual" loading pattern (data-manual-src / data-manual-srcset).
 * Acts as a universal, viewport-aware fallback that triggers each image the moment it approaches
 * the visible area — complementing the native loading="lazy" attribute already set by the theme.
 *
 * The theme's snippets/image.liquid outputs data-manual-src when loading="manual" is passed, and
 * individual section scripts call theme.manuallyLoadImages() to resolve those images. This observer
 * ensures any that are missed (e.g. in dynamically injected content or edge-case section order)
 * are still loaded smoothly as the user scrolls.
 */
(function () {
  if (!('IntersectionObserver' in window)) return;

  function observeManualImages(root) {
    const images = (root || document).querySelectorAll('img[data-manual-src]');
    if (!images.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          img.src = img.dataset.manualSrc;
          img.removeAttribute('data-manual-src');
          if (img.dataset.manualSrcset) {
            img.srcset = img.dataset.manualSrcset;
            img.removeAttribute('data-manual-srcset');
          }
          obs.unobserve(img);
        });
      },
      { rootMargin: '400px 0px' }
    );

    images.forEach((img) => observer.observe(img));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observeManualImages(document));
  } else {
    observeManualImages(document);
  }

  // Re-run when the theme injects new content (quick buy, cart drawer, etc.)
  document.addEventListener('on:quickbuy:after-open', (e) => {
    if (e.detail && e.detail.container) observeManualImages(e.detail.container);
  });
})();

/**
 * Ensure original_price is visible on initial page load (when variant defaults to LGE)
 * Only show if original_price is different from current price
 */
document.addEventListener('DOMContentLoaded', () => {
  const priceContainer = document.querySelector('.product-info__price');
  
  if (!priceContainer) return;
  
  const originalPriceFormatted = priceContainer.dataset.originalPrice;
  
  if (!originalPriceFormatted || originalPriceFormatted === '') return;
  
  const priceElement = priceContainer.querySelector('.price');
  const priceWasEl = priceContainer.querySelector('.price__was');
  const priceCurrentEl = priceContainer.querySelector('.price__current');
  
  if (!priceElement || !priceWasEl || !priceCurrentEl) return;
  
  // Extract numeric values for comparison
  const originalPriceValue = extractNumericPrice(originalPriceFormatted);
  const currentPriceValue = extractNumericPrice(priceCurrentEl.textContent);
  
  // Only show the "was" price if it's greater than current price
  if (originalPriceValue && currentPriceValue && originalPriceValue > currentPriceValue) {
    if (priceWasEl.innerHTML === '' || priceWasEl.innerHTML.trim() === '') {
      priceWasEl.innerHTML = originalPriceFormatted;
      priceElement.classList.add('price--on-sale');
    }
  } else {
    // Prices are equal or original is lower, ensure "was" price is hidden
    priceWasEl.innerHTML = '';
    priceElement.classList.remove('price--on-sale');
  }
});

/**
 * =================================================================================================
 * Size Chart - Re-initialize interactive tabs/spans after quick buy injects content
 * =================================================================================================
 *
 * size-guide-body.liquid contains an inline <script> that wraps all logic in DOMContentLoaded.
 * The theme's quick buy loader only re-executes external script[src] tags — inline scripts are
 * never run when content is cloned from a <template> and appended. DOMContentLoaded has also
 * already fired by then, so the callback would never execute regardless.
 *
 * Solution: listen for on:quickbuy:after-open and reinitialise the size chart UI, scoped to the
 * quick buy container so it doesn't conflict with any size chart already on the product page.
 */
function initSizeChart(root) {
  root = root || document;

  root.querySelectorAll('.size-chart-tab').forEach((tabButton) => {
    tabButton.addEventListener('click', () => {
      if (tabButton.classList.contains('active')) return;
      const prevTabBtn = root.querySelector('.size-chart-tab.active');
      const prevTabCnt = root.querySelector('.size-chart-tab-content.active');
      if (prevTabBtn) prevTabBtn.classList.remove('active');
      if (prevTabCnt) prevTabCnt.classList.remove('active');
      tabButton.classList.add('active');
      const checkboxWrap = root.querySelector('.size-chart-checkbox');
      if (checkboxWrap) checkboxWrap.style.display = 'block';
      const targetCnt = root.querySelector(`.size-chart-tab-content[data-handle="${tabButton.dataset.target}"]`);
      if (targetCnt) {
        targetCnt.classList.add('active');
        targetCnt.querySelector('.size-chart-content-grid span.active')?.click();
      }
    });
  });

  root.querySelectorAll('.size-chart-content-grid span').forEach((size) => {
    size.addEventListener('click', () => {
      const checkboxWrap = root.querySelector('.size-chart-checkbox');
      const caseModifier = size.dataset.caseModifier || '';
      const sizeResult = root.querySelector('.size-chart-result span');
      const tabContent = size.closest('.size-chart-tab-content');
      const prevActive = tabContent ? tabContent.querySelector('.size-chart-content-grid span.active') : null;
      const clearCase = root.querySelector('#size-chart-checkbox')?.checked;
      let sizeValue = size.dataset.value;

      if (caseModifier.toLowerCase() === 'na') {
        if (checkboxWrap) checkboxWrap.style.display = 'none';
      } else {
        if (checkboxWrap) checkboxWrap.style.display = 'block';
        if (clearCase && caseModifier === 'next') {
          if (sizeValue === 'small') sizeValue = 'medium';
          else if (sizeValue === 'medium') sizeValue = 'large';
        }
      }

      if (sizeResult) sizeResult.innerText = sizeValue;
      if (size.classList.contains('active')) return;
      if (prevActive) prevActive.classList.remove('active');
      size.classList.add('active');
    });
  });

  const checkbox = root.querySelector('#size-chart-checkbox');
  if (checkbox) {
    checkbox.addEventListener('change', (e) => {
      const activeTab = root.querySelector('.size-chart-tab-content.active');
      if (!activeTab) return;
      const activeSpan = activeTab.querySelector('span.active');
      const activeSizeResult = activeSpan?.dataset.value;
      const activeCaseModifier = activeSpan?.dataset.caseModifier;
      const visibleResult = root.querySelector('.size-chart-result span');
      if (!visibleResult || !activeSizeResult) return;
      if (e.target.checked && activeCaseModifier === 'next') {
        if (activeSizeResult === 'medium') visibleResult.textContent = 'large';
        else if (activeSizeResult === 'small') visibleResult.textContent = 'medium';
      } else {
        visibleResult.textContent = activeSizeResult;
      }
    });
  }
}

document.addEventListener('on:quickbuy:after-open', (event) => {
  initSizeChart(event.target);
});

/**
 * =================================================================================================
 * Custom Megamenu — Mobile: navigation links navigate, toggle button opens submenu
 * =================================================================================================
 *
 * The theme's mobile drawer JS (main.js) listens on the mobileDrawer element during the bubble
 * phase and calls evt.preventDefault() on any .navigation__item--with-children > .navigation__link
 * click, redirecting it to open the submenu instead of following the href.
 *
 * For .custom-megamenu--mobile we want the opposite split:
 *   - Click on .navigation__link           → follow the href (navigate to the page)
 *   - Click on .navigation__children-toggle → open the submenu (theme default, untouched)
 *
 * Registering in the CAPTURE phase on document means this handler fires before the theme's
 * bubble-phase delegated listener on mobileDrawer.  stopImmediatePropagation() prevents the event
 * from propagating further so the theme never sees it.  preventDefault() is deliberately NOT called
 * so the browser follows the link's href naturally.
 */
document.addEventListener('click', function(e) {
  const link = e.target.closest('.custom-megamenu--mobile .navigation__link');
  if (!link) return;
  e.stopImmediatePropagation();
  // No preventDefault() — browser navigates to link href.
}, true);
